import { getOutputNodeInputType, isOutputNode } from './nodeDefs.js';
import type { PortDef, SwitchNodeData, SwitchValueType } from './nodeDefs.js';
import { defineRenderNode, type RenderNodeDefsSubset } from './renderNodeDefs.js';

const SWITCH_TYPE_OPTIONS = [
  { label: 'Float', value: 'float' },
  { label: 'Vec2', value: 'vec2' },
  { label: 'Vec3', value: 'vec3' },
  { label: 'Vec4', value: 'vec4' },
] as const;

const SWITCH_DEFAULT_COUNT = 2;
const SWITCH_MAX_COUNT = 16;

const SWITCH_COUNT_OPTIONS = Array.from({ length: SWITCH_MAX_COUNT - SWITCH_DEFAULT_COUNT + 1 }, (_, index) => {
  const count = SWITCH_DEFAULT_COUNT + index;
  return { label: String(count), value: String(count) };
});

const SWITCH_INPUT_PORT_ROWS = Array.from({ length: SWITCH_MAX_COUNT }, (_, index) => ({
  kind: 'port',
  ref: `in${index}`,
} as const));

export function switchValueType(value: unknown): SwitchValueType {
  return value === 'float' || value === 'vec2' || value === 'vec3' || value === 'vec4' ? value : 'vec4';
}

export function switchCount(value: unknown): number {
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? Math.max(SWITCH_DEFAULT_COUNT, Math.min(SWITCH_MAX_COUNT, n)) : SWITCH_DEFAULT_COUNT;
}

export const SPECIAL_NODE_DEFS: RenderNodeDefsSubset<
  | 'ValueNoise'
  | 'Perlin'
  | 'Fbm'
  | 'Turbulence'
  | 'Colorize'
  | 'Switch'
  | 'Output'
> = {
  ValueNoise: defineRenderNode({
    label: 'Value Noise', cat: 'special',
    inputs: [{ name: 'point', label: 'Point', type: 'vec2' }, { name: 'seed', label: 'Seed', type: 'float', range: { min: -100, max: 100, step: 0.1 } }, { name: 'time', label: 'Time', type: 'float' }],
    outputs: [{ name: 'n', type: 'float' }],
  }),
  Perlin: defineRenderNode({
    label: 'Perlin', cat: 'special',
    inputs: [{ name: 'point', label: 'Point', type: 'vec2' }, { name: 'seed', label: 'Seed', type: 'float', range: { min: -100, max: 100, step: 0.1 } }, { name: 'time', label: 'Time', type: 'float' }],
    outputs: [{ name: 'n', type: 'float' }],
  }),
  Fbm: defineRenderNode({
    label: 'fBm', cat: 'special',
    inputs: [
      { name: 'point', label: 'Point', type: 'vec2' },
      { name: 'seed', label: 'Seed', type: 'float', range: { min: -100, max: 100, step: 0.1 } },
      { name: 'time', label: 'Time', type: 'float' },
      { name: 'octaves', label: 'Octaves', type: 'float', range: { min: 1, max: 16, step: 1 } },
      { name: 'lacunarity', label: 'Lacunarity', type: 'float', range: { min: -100, max: 100, step: 0.1, subtype: 'factor' } },
      { name: 'gain', label: 'Gain', type: 'float', range: { min: 0, max: 1, step: 0.05, subtype: 'factor' } }
    ],
    outputs: [{ name: 'n', type: 'float' }],
  }),
  Turbulence: defineRenderNode({
    label: 'Turbulence', cat: 'special',
    inputs: [
      { name: 'point', label: 'Point', type: 'vec2' },
      { name: 'seed', label: 'Seed', type: 'float', range: { min: -100, max: 100, step: 0.1 } },
      { name: 'time', label: 'Time', type: 'float' },
      { name: 'octaves', label: 'Octaves', type: 'float', range: { min: 1, max: 16, step: 1 } },
      { name: 'lacunarity', label: 'Lacunarity', type: 'float', range: { min: -100, max: 100, step: 0.1, subtype: 'factor' } },
      { name: 'gain', label: 'Gain', type: 'float', range: { min: 0, max: 1, step: 0.05, subtype: 'factor' } }
    ],
    outputs: [{ name: 'n', type: 'float' }],
  }),
  Colorize: defineRenderNode({
    label: 'Colorize', cat: 'special',
    inputs: [{ name: 'value', label: 'Value', type: 'float' }],
    outputs: [{ name: 'color', label: 'Color', type: 'vec4' }],
  }),
  Switch: {
    label: 'Switch', cat: 'special',
    inputs: (node): PortDef[] => {
      const data = node.data as SwitchNodeData;
      const type = switchValueType(data.valueType);
      const count = switchCount(data.count);
      const ins: PortDef[] = [{ name: 'index', label: 'Index', type: 'float', range: { min: 0, max: count - 1, step: 1 } }];
      for (let i = 0; i < count; i++) ins.push({ name: `in${i}`, label: String(i), type });
      return ins;
    },
    outputs: (node): PortDef[] => [{ name: 'out', label: 'Out', type: switchValueType((node.data as SwitchNodeData).valueType) }],
    graphs: ['material'],
    minWidth: 150,
    ui: [
      { kind: 'port', ref: 'index' },
      { kind: 'select', field: 'valueType', label: 'Type', options: SWITCH_TYPE_OPTIONS, default: 'vec4' },
      { kind: 'select', field: 'count', label: 'Count', options: SWITCH_COUNT_OPTIONS, default: String(SWITCH_DEFAULT_COUNT) },
      ...SWITCH_INPUT_PORT_ROWS,
      { kind: 'port', ref: 'out' },
    ]
  },
  Output: {
    label: 'Output', cat: 'output',
    inputs: node => {
      const inputName = isOutputNode(node) ? node.data.inputName : 'value';
      const inputType = getOutputNodeInputType(node);
      const primary = {
        name: inputName,
        label: inputType === 'shape' ? 'Shape' : inputType === 'float' ? 'Distance' : 'Color',
        type: inputType,
      } as const;
      if (inputType === 'shape') return [primary];
      if (inputType === 'float') return [primary];
      return [primary, { name: 'drawBounds', label: 'Draw bounds', type: 'bounds' as const }];
    },
    outputs: [],
  }
};

(['ValueNoise', 'Perlin', 'Fbm', 'Turbulence', 'Colorize'] as const).forEach(key => {
  SPECIAL_NODE_DEFS[key].graphs = ['material'];
});
